import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

/**
 * Stage Cards component for displaying playbook stages with navigation
 * @param {Object} props - Component props
 * @param {Array} props.stages - Array of stage objects with id, title, description
 * @param {Object} props.icons - Object mapping stage ids to icon components
 * @param {string} props.playbookId - ID of the current playbook
 * @param {string} props.introText - Optional introduction text to display above the cards
 */
const StageCards = ({ stages, icons, playbookId, introText }) => {
  // Default color palette from design system
  // Uses consistent backgrounds to match individual stage styling
  const defaultGradients = {
    'stage-1': 'from-gray-800/50 to-gray-700/50',
    'stage-2': 'from-gray-800/50 to-gray-700/50',
    'stage-3': 'from-gray-800/50 to-gray-700/50',
    'stage-4': 'from-gray-800/50 to-gray-700/50',
    'stage-5': 'from-gray-800/50 to-gray-700/50',
    'stage-6': 'from-gray-800/50 to-gray-700/50',
    default: 'from-gray-800/50 to-gray-700/50'
  };

  // Allow component users to gracefully handle various input types
  const stageItems = Array.isArray(stages) ? stages : [];
  
  return (
    <>
      {introText && (
        <div className="mb-6 text-base md:text-lg text-gray-300">
          <p>{introText}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {stageItems.map((stage, index) => {
          const stageNumber = index + 1;
          const stageId = stage.id;
          const stageIcon = icons?.[stageId] || null;
          const gradientClass = defaultGradients[stageId] || defaultGradients.default;
          
          return (
            <Link 
              key={stageId} 
              to={`/playbooks/${playbookId}/${stageId}`}
              className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            >
              <div className={`relative p-5 rounded-lg border border-gray-700/50 bg-gradient-to-br ${gradientClass} hover:border-gray-600 hover:shadow-lg transition-all duration-200 h-full flex flex-col`}>
                <div className="flex items-center mb-3">
                  {stageIcon && (
                    <div className="bg-gray-800 rounded-full p-2 mr-3 text-primary-light">
                      {stageIcon}
                    </div>
                  )}
                  <h3 className="font-medium text-lg text-white">
                    {stage.title}
                  </h3>
                </div>
                
                <p className="text-gray-300 text-sm mb-4 flex-grow">
                  {stage.description}
                </p>
                
                <div className="flex items-center text-primary text-sm transition-all group-hover:translate-x-1 duration-200">
                  <span className="mr-1">View details</span>
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
};

export default StageCards; 